// SCR-STAFF-504 (US-CLN-052، 053، 110، 111، 112؛ FLOW-08): ثبت جلسه درمان زیبایی در مواجهه Session/Procedure — طرح و شماره جلسه، نواحی روی
// نقشه بدن (COMP-BODY-MAP)، دستگاه و شمارنده (COMP-DEVICE-USAGE)، پارامترهای پیش‌پر از آخرین جلسه هم‌ناحیه و هم‌دستگاه با برجسته‌سازی تغییر و
// هشدار محدوده فیتزپاتریک (دلیل الزامی)، نقشه تزریق با محصول، بچ (منقضی انتخاب‌پذیر نیست) و دوز، و اصلاح ۲۴ساعته با دلیل.
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type ClnEncounterView,
  type ClnInjectionDepth,
  type ClnInjectionPointRequest,
  type ClnInjectionTechnique,
  type ClnInjectionUnit,
  type ClnParameterDefinitionView,
  type ClnSessionDefaultsView,
  type ClnTreatmentSessionRequest,
  type ClnTreatmentSessionView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, latinDigits, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { BodyMap, type BodyMapPoint } from './BodyMap';
import { AdverseEventDialog } from './AdverseEvents';
import {
  beautyKeys,
  doseTotals,
  INJECTION_DEPTHS,
  INJECTION_TECHNIQUES,
  ParameterStatusChip,
  parameterSummary,
  rangeStatus,
  SCR_STAFF_504,
  useItemBatches,
  useItemSearch,
  type ItemOption,
} from './beautyCommon';
import { clnKeys, MaybeSample, num } from './common';
import { SkinAssessmentDialog } from './SkinAssessmentDialog';
import { DeviceUsageField, type DeviceUsageValue } from '../organization/deviceCommon';

interface ParamInput {
  value: string;
  reason: string;
  /** کاربر مقدار را دستی تغییر داده (پیش‌پر بعدی آن را بازنویسی نمی‌کند). */
  touched: boolean;
}

interface PointDraft extends ClnInjectionPointRequest {
  itemName: string;
}

interface Template {
  item: ItemOption | null;
  batchId: string;
  dose: string;
  unit: ClnInjectionUnit;
  depth: string;
  technique: string;
}

const EMPTY_TEMPLATE: Template = { item: null, batchId: '', dose: '', unit: 'Unit', depth: '', technique: '' };

function toNumber(text: string): number | null {
  const normalized = latinDigits(text).replace(/[^\d.]/g, '');
  return normalized === '' || Number.isNaN(Number(normalized)) ? null : Number(normalized);
}

/** پارامترهای ورودی ← درخواست (کلیدهای مجموعه‌پارامتر). */
export function parameterRequests(definitions: readonly ClnParameterDefinitionView[], inputs: Readonly<Record<string, ParamInput>>) {
  return definitions
    .map((definition) => {
      const input = inputs[definition.key];
      if (!input || input.value.trim() === '') {
        return null;
      }
      const numeric = definition.type === 'Number';
      return {
        key: definition.key,
        value: numeric ? toNumber(input.value) : null,
        text: numeric ? null : input.value.trim(),
        reason: input.reason.trim() || null,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}

function ParameterRow({
  definition,
  input,
  previous,
  onChange,
}: {
  definition: ClnParameterDefinitionView;
  input: ParamInput | undefined;
  previous: string | null;
  onChange: (next: ParamInput) => void;
}) {
  const { t } = useTranslation('cln');
  const value = input?.value ?? '';
  const numeric = definition.type === 'Number';
  const status = numeric ? rangeStatus(toNumber(value), definition) : 'Unknown';
  const changed = previous !== null && value !== '' && previous !== value;
  const needsReason = status === 'OutsideRecommended' || status === 'OutsideAbsolute';
  const hint = [
    num(definition.recommendedMin) !== null || num(definition.recommendedMax) !== null
      ? t('cln:session.recommended', { min: faDigits(num(definition.recommendedMin) ?? '—'), max: faDigits(num(definition.recommendedMax) ?? '—') })
      : null,
    num(definition.absoluteMin) !== null || num(definition.absoluteMax) !== null
      ? t('cln:session.absolute', { min: faDigits(num(definition.absoluteMin) ?? '—'), max: faDigits(num(definition.absoluteMax) ?? '—') })
      : null,
    previous !== null ? t('cln:session.previous', { value: faDigits(previous) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Stack spacing={1} data-testid={`cln-param-${definition.key}`} data-changed={changed ? 'true' : 'false'} data-status={status}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        {definition.options && definition.options.length > 0 ? (
          <TextField
            select
            fullWidth
            size="small"
            label={`${definition.name}${definition.unit ? ` (${definition.unit})` : ''}`}
            value={value}
            onChange={(event) => onChange({ value: event.target.value, reason: input?.reason ?? '', touched: true })}
            helperText={hint || undefined}
            slotProps={{ htmlInput: { 'data-testid': `cln-param-input-${definition.key}` } }}
          >
            <MenuItem value="">—</MenuItem>
            {definition.options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            fullWidth
            size="small"
            label={`${definition.name}${definition.unit ? ` (${definition.unit})` : ''}`}
            value={numeric && value ? faDigits(value) : value}
            onChange={(event) => onChange({ value: numeric ? latinDigits(event.target.value).replace(/[^\d.]/g, '') : event.target.value, reason: input?.reason ?? '', touched: true })}
            helperText={hint || undefined}
            sx={changed ? { '& .MuiOutlinedInput-root': { bgcolor: 'warning.light' } } : undefined}
            slotProps={{ htmlInput: { inputMode: numeric ? 'decimal' : 'text', 'data-testid': `cln-param-input-${definition.key}` } }}
          />
        )}
        <Box sx={{ pt: 1 }}>
          <ParameterStatusChip status={status} />
          {changed && <Chip size="small" color="info" variant="outlined" label={t('cln:session.changed')} sx={{ marginInlineStart: 0.5 }} data-testid={`cln-param-changed-${definition.key}`} />}
        </Box>
      </Stack>
      {needsReason && (
        <TextField
          fullWidth
          size="small"
          required
          error={!input?.reason.trim()}
          label={t('cln:session.reason')}
          helperText={t(`cln:session.reason_hint.${status}`)}
          value={input?.reason ?? ''}
          onChange={(event) => onChange({ value, reason: event.target.value, touched: true })}
          slotProps={{ htmlInput: { maxLength: 500, 'data-testid': `cln-param-reason-${definition.key}` } }}
        />
      )}
    </Stack>
  );
}

function InjectionTemplateBar({ template, onChange }: { template: Template; onChange: (next: Template) => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi } = useTenantSession();
  const search = useItemSearch();
  const batches = useItemBatches(template.item?.id ?? null);
  const [scan, setScan] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const resolve = useMutation({
    mutationFn: (raw: string) => unwrap(tenantApi.POST('/api/v1/inventory/scans:resolve', { body: { raw } })),
    onSuccess: (result) => {
      setScan('');
      if (!result.item) {
        setScanError(t('cln:injection.scan_unknown'));
        return;
      }
      setScanError(result.batch && result.batch.isSelectable === false ? t('cln:injection.batch_not_selectable') : null);
      onChange({
        ...template,
        item: { id: result.item.itemId, name: result.item.name, code: result.item.code, tracksBatch: result.item.tracksBatch },
        batchId: result.batch && result.batch.isSelectable !== false ? result.batch.batchId : '',
      });
    },
    onError: () => setScanError(t('cln:injection.scan_unknown')),
  });
  return (
    <Paper variant="outlined" sx={{ p: 1 }} data-testid="cln-injection-template">
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            size="small"
            options={search.items}
            value={template.item}
            filterOptions={(options) => options}
            loading={search.isFetching}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            onInputChange={(_event, value, reason) => reason === 'input' && search.search(value)}
            onChange={(_event, value) => onChange({ ...template, item: value, batchId: '' })}
            renderInput={(params) => <TextField {...params} label={t('cln:injection.product')} slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'cln-injection-product' } }} />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label={t('cln:injection.scan')}
            value={scan}
            onChange={(event) => setScan(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && scan.trim()) {
                event.preventDefault();
                resolve.mutate(scan.trim());
              }
            }}
            helperText={scanError ?? t('cln:injection.scan_hint')}
            error={Boolean(scanError)}
            slotProps={{ htmlInput: { 'data-testid': 'cln-injection-scan' } }}
          />
        </Grid>
        {template.item?.tracksBatch && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('cln:injection.batch')}
              value={template.batchId}
              onChange={(event) => onChange({ ...template, batchId: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'cln-injection-batch' } }}
            >
              {(batches.data?.items ?? []).map((batch) => (
                <MenuItem key={batch.id} value={batch.id} disabled={!batch.isSelectable} data-testid={`cln-batch-${batch.id}`} data-selectable={batch.isSelectable ? 'true' : 'false'}>
                  {batch.batchNumber}
                  {batch.expiryDate ? ` · ${batch.expiryDate}` : ''}
                  {!batch.isSelectable ? ` · ${t('cln:injection.batch_not_selectable')}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            required
            label={t('cln:injection.dose')}
            value={template.dose ? faDigits(template.dose) : ''}
            onChange={(event) => onChange({ ...template, dose: latinDigits(event.target.value).replace(/[^\d.]/g, '') })}
            slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'cln-injection-dose' } }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField select fullWidth size="small" label={t('cln:injection.unit')} value={template.unit} onChange={(event) => onChange({ ...template, unit: event.target.value as ClnInjectionUnit })}>
            <MenuItem value="Unit">{t('cln:injection_unit.Unit')}</MenuItem>
            <MenuItem value="Milliliter">{t('cln:injection_unit.Milliliter')}</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField select fullWidth size="small" label={t('cln:injection.depth')} value={template.depth} onChange={(event) => onChange({ ...template, depth: event.target.value })}>
            <MenuItem value="">—</MenuItem>
            {INJECTION_DEPTHS.map((depth) => (
              <MenuItem key={depth} value={depth}>
                {t(`cln:injection_depth.${depth}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField select fullWidth size="small" label={t('cln:injection.technique')} value={template.technique} onChange={(event) => onChange({ ...template, technique: event.target.value })}>
            <MenuItem value="">—</MenuItem>
            {INJECTION_TECHNIQUES.map((technique) => (
              <MenuItem key={technique} value={technique}>
                {t(`cln:injection_technique.${technique}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Paper>
  );
}

/** کارت جلسه ثبت‌شده. */
function SessionCard({ session, onCorrect, onAdverse }: { session: ClnTreatmentSessionView; onCorrect?: () => void; onAdverse?: () => void }) {
  const { t } = useTranslation('cln');
  const { timeZone } = useDisplaySettings();
  const number =
    session.numberInStage !== null && session.numberInStage !== undefined
      ? t('cln:session.number_of', { n: faDigits(num(session.numberInStage) ?? 0), total: faDigits(num(session.stageSessions) ?? 0) })
      : session.packageSessionNumber !== null && session.packageSessionNumber !== undefined
        ? t('cln:session.package_number', { n: faDigits(num(session.packageSessionNumber) ?? 0), total: faDigits(num(session.packageSessions) ?? 0) })
        : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-session-card" data-session-id={session.id}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2">{session.planTitle ?? t('cln:session.title')}</Typography>
        {number && <Chip size="small" label={number} data-testid="cln-session-number" />}
        {session.fitzpatrick && <Chip size="small" variant="outlined" label={t('cln:skin.fitzpatrick_value', { value: session.fitzpatrick })} />}
        {num(session.correctionCount) ? <Chip size="small" color="info" variant="outlined" label={t('cln:session.corrected')} data-testid="cln-session-corrected" /> : null}
        {session.locked && <Chip size="small" variant="outlined" label={t('cln:session.locked')} />}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {toJalaliDateTime(session.completedAt, { timeZone })}
        </Typography>
      </Stack>
      {session.areas.length > 0 && (
        <Typography variant="body2">
          {t('cln:session.areas')}: {session.areas.map((area) => t(`cln:body_area.${area}`)).join(t('cln:common.list_separator'))}
        </Typography>
      )}
      {session.parameters.length > 0 && <Typography variant="body2">{parameterSummary(session.parameters)}</Typography>}
      {session.devices.map((device) => (
        <Typography key={device.deviceId} variant="body2" color="text.secondary">
          {device.deviceName ?? ''} · {t('cln:session.counter', { start: faDigits(num(device.counterStart) ?? 0), end: faDigits(num(device.counterEnd) ?? 0), quantity: faDigits(num(device.quantity) ?? 0) })}
        </Typography>
      ))}
      {session.points.length > 0 && (
        <Typography variant="body2">
          {t('cln:injection.summary', { count: faDigits(session.points.length) })} ·{' '}
          {Object.entries(doseTotals(session.points.map((point) => ({ dose: point.dose, unit: point.unit }))))
            .filter(([, total]) => total > 0)
            .map(([unit, total]) => `${faDigits(total)} ${t(`cln:injection_unit.${unit}`)}`)
            .join(' · ')}
        </Typography>
      )}
      {session.warnings.map((warning) => (
        <Alert key={`${warning.code}-${warning.unit ?? ''}`} severity="warning" sx={{ mt: 1 }} data-testid={`cln-session-warning-${warning.code}`}>
          {t(`cln:session.warning.${warning.code}`, {
            unit: warning.unit ? t(`cln:injection_unit.${warning.unit}`) : '',
            expected: faDigits(num(warning.expected) ?? 0),
            recorded: faDigits(num(warning.recorded) ?? 0),
          })}
        </Alert>
      ))}
      {(session.immediateReaction || session.outcome) && (
        <Typography variant="body2" color="text.secondary">
          {[session.immediateReaction, session.outcome].filter(Boolean).join(' · ')}
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {onCorrect && session.canCorrect && !session.locked && (
          <Button size="small" onClick={onCorrect} data-testid="cln-session-correct">
            {t('cln:session.correct')}
          </Button>
        )}
        {onAdverse && (
          <Button size="small" color="warning" onClick={onAdverse} data-testid="cln-session-adverse">
            {t('cln:adverse.report')}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export interface TreatmentSessionPanelProps {
  encounter: ClnEncounterView;
  onChanged?: () => void;
}

export function TreatmentSessionPanel({ encounter, onChanged }: TreatmentSessionPanelProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session: auth } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = auth?.tenantId;
  const canRecord = access.accessMode === 'Full' && access.can('cln.session.record') && encounter.status !== 'Cancelled';
  const canReport = access.accessMode === 'Full' && access.can('cln.adverse_event.report');
  const serviceId = encounter.services.find((service) => service.status === 'Rendered')?.serviceId ?? null;

  const [editing, setEditing] = useState<ClnTreatmentSessionView | 'new' | null>(null);
  const [planId, setPlanId] = useState('');
  const [areas, setAreas] = useState<string[]>([]);
  const [device, setDevice] = useState<DeviceUsageValue | null>(null);
  const [inputs, setInputs] = useState<Record<string, ParamInput>>({});
  const [mode, setMode] = useState<'areas' | 'injection'>('areas');
  const [points, setPoints] = useState<PointDraft[]>([]);
  const [template, setTemplate] = useState<Template>(EMPTY_TEMPLATE);
  const [reaction, setReaction] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [outcome, setOutcome] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [skinOpen, setSkinOpen] = useState(false);
  const [adverseFor, setAdverseFor] = useState<string | null>(null);
  const [pointError, setPointError] = useState<string | null>(null);

  const sessions = useQuery({
    queryKey: beautyKeys.sessions(tenantId, encounter.id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/encounters/{id}/treatment-sessions', { params: { path: { id: encounter.id } } })),
  });
  const areaKey = [...areas].sort().join(',');
  const defaults = useQuery({
    queryKey: beautyKeys.defaults(tenantId, encounter.id, `${device?.deviceId ?? ''}|${serviceId ?? ''}|${areaKey}`),
    enabled: editing !== null,
    placeholderData: (previous) => previous,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/clinical/encounters/{id}/session-defaults', {
          params: { path: { id: encounter.id }, query: { deviceId: device?.deviceId || undefined, serviceId: serviceId ?? undefined, areas: areaKey || undefined } },
        }),
      ),
  });
  const data: ClnSessionDefaultsView | undefined = defaults.data;
  const definitions = useMemo(() => data?.parameterSet?.parameters ?? [], [data]);
  const previousByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of data?.prefill ?? []) {
      if (item.value !== null && item.value !== undefined) {
        map.set(item.key, String(Number(item.value)));
      } else if (item.text) {
        map.set(item.key, item.text);
      }
    }
    return map;
  }, [data]);

  // پیش‌پر از آخرین جلسه هم‌ناحیه و هم‌دستگاه (AC-CLN-053.1)؛ مقدار دستی کاربر بازنویسی نمی‌شود.
  useEffect(() => {
    if (editing !== 'new' || previousByKey.size === 0) {
      return;
    }
    setInputs((current) => {
      const next = { ...current };
      for (const [paramKey, value] of previousByKey) {
        if (!next[paramKey]?.touched) {
          next[paramKey] = { value, reason: '', touched: false };
        }
      }
      return next;
    });
  }, [previousByKey, editing]);

  // تنها طرح فعال پیش‌فرض است.
  useEffect(() => {
    if (editing === 'new' && !planId && data?.plans.length === 1 && data.plans[0]) {
      setPlanId(data.plans[0].planId);
    }
  }, [data, editing, planId]);

  const reset = () => {
    setEditing(null);
    setPlanId('');
    setAreas([]);
    setDevice(null);
    setInputs({});
    setPoints([]);
    setTemplate(EMPTY_TEMPLATE);
    setReaction('');
    setRecommendations('');
    setOutcome('');
    setCorrectionReason('');
    setPointError(null);
    setMode('areas');
  };

  const startCorrection = (view: ClnTreatmentSessionView) => {
    setEditing(view);
    setPlanId(view.planId ?? '');
    setAreas([...view.areas]);
    const first = view.devices[0];
    setDevice(first ? { deviceId: first.deviceId, quantity: num(first.quantity) ?? 0, counterStart: num(first.counterStart), counterEnd: num(first.counterEnd), note: first.note ?? '' } : null);
    setInputs(
      Object.fromEntries(
        view.parameters.map((parameter) => [
          parameter.key,
          { value: parameter.value !== null && parameter.value !== undefined ? String(Number(parameter.value)) : (parameter.text ?? ''), reason: parameter.reason ?? '', touched: true },
        ]),
      ),
    );
    setPoints(
      view.points.map((point) => ({
        x: Number(point.x),
        y: Number(point.y),
        areaCode: point.areaCode,
        itemId: point.itemId,
        itemName: point.itemName ?? '',
        batchId: point.batchId,
        batchNumber: point.batchNumber,
        expiryDate: point.expiryDate,
        dose: Number(point.dose),
        unit: point.unit,
        depth: point.depth,
        technique: point.technique,
        note: point.note,
      })),
    );
    setMode(view.points.length > 0 ? 'injection' : 'areas');
    setReaction(view.immediateReaction ?? '');
    setRecommendations(view.recommendations ?? '');
    setOutcome(view.outcome ?? '');
    setCorrectionReason('');
  };

  const fitzpatrickMissing = Boolean(data?.requiresFitzpatrick && !data.fitzpatrick);
  const parameterList = parameterRequests(definitions, inputs);
  const missingReason = definitions.some((definition) => {
    const status = definition.type === 'Number' ? rangeStatus(toNumber(inputs[definition.key]?.value ?? ''), definition) : 'Unknown';
    return (status === 'OutsideRecommended' || status === 'OutsideAbsolute') && !inputs[definition.key]?.reason.trim();
  });
  const totals = doseTotals(points);
  const expected = data?.expectedDoses ?? [];
  const mismatches = expected.filter((item) => Math.abs((totals[item.unit] ?? 0) - Number(item.expected)) > 0.0001 && points.length > 0);
  const correcting = editing !== null && editing !== 'new';

  const body = (): ClnTreatmentSessionRequest => {
    const option = data?.plans.find((plan) => plan.planId === planId);
    return {
      planId: planId || null,
      stageId: option?.stageId ?? null,
      serviceId,
      areas,
      devices: device?.deviceId
        ? [{ deviceId: device.deviceId, quantity: null, counterStart: device.counterStart, counterEnd: device.counterEnd, note: device.note.trim() || null }]
        : [],
      parameters: parameterList,
      mapKind: points.length > 0 ? 'Face' : null,
      points: points.map(({ itemName: _name, ...point }) => point),
      immediateReaction: reaction.trim() || null,
      recommendations: recommendations.trim() || null,
      outcome: outcome.trim() || null,
      correctionReason: correcting ? correctionReason.trim() || null : null,
    };
  };

  const save = useMutation({
    mutationFn: () =>
      correcting
        ? unwrap(tenantApi.PUT('/api/v1/clinical/treatment-sessions/{id}', { params: { path: { id: editing.id } }, body: body() }))
        : unwrap(
            tenantApi.POST('/api/v1/clinical/encounters/{id}/treatment-sessions', {
              params: { path: { id: encounter.id } },
              headers: { 'Idempotency-Key': key.current() },
              body: body(),
            }),
          ),
    onSettled: (_data, error) => key.settle(error),
    onSuccess: () => {
      reset();
      void queryClient.invalidateQueries({ queryKey: beautyKeys.sessions(tenantId, encounter.id) });
      void queryClient.invalidateQueries({ queryKey: clnKeys.patient(tenantId, encounter.patientId) });
      onChanged?.();
    },
  });

  const addPoint = (point: { x: number; y: number; area: string | null }) => {
    if (!template.item) {
      setPointError(t('cln:injection.pick_product'));
      return;
    }
    const dose = toNumber(template.dose);
    if (dose === null || dose <= 0) {
      setPointError(t('cln:injection.pick_dose'));
      return;
    }
    if (template.item.tracksBatch && !template.batchId) {
      setPointError(t('cln:injection.pick_batch'));
      return;
    }
    setPointError(null);
    setPoints((current) => [
      ...current,
      {
        x: point.x,
        y: point.y,
        areaCode: point.area,
        itemId: template.item?.id ?? '',
        itemName: template.item?.name ?? '',
        batchId: template.batchId || null,
        batchNumber: null,
        expiryDate: null,
        dose,
        unit: template.unit,
        depth: (template.depth || null) as ClnInjectionDepth,
        technique: (template.technique || null) as ClnInjectionTechnique,
        note: null,
      },
    ]);
  };

  const loadPrevious = () =>
    setPoints(
      (data?.lastInjectionMap ?? []).map((point) => ({
        x: Number(point.x),
        y: Number(point.y),
        areaCode: point.areaCode,
        itemId: point.itemId,
        itemName: point.itemName ?? '',
        batchId: null,
        batchNumber: null,
        expiryDate: null,
        dose: Number(point.dose),
        unit: point.unit,
        depth: point.depth,
        technique: point.technique,
        note: point.note,
      })),
    );

  const mapPoints: BodyMapPoint[] = points.map((point, index) => ({ x: Number(point.x), y: Number(point.y), label: faDigits(index + 1) }));
  const blocked = fitzpatrickMissing || missingReason || (correcting && !correctionReason.trim()) || (areas.length === 0 && points.length === 0);
  const plan = data?.plans.find((option) => option.planId === planId);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-screen-id={SCR_STAFF_504} data-testid="cln-session-panel">
      <Stack direction="row" sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
          {t('cln:session.title')}
        </Typography>
        {canRecord && editing === null && (
          <Button variant="outlined" onClick={() => setEditing('new')} data-testid="cln-session-new">
            {t('cln:session.new')}
          </Button>
        )}
      </Stack>

      <Stack spacing={1}>
        {(sessions.data?.items ?? []).map((item) => (
          <SessionCard key={item.id} session={item} onCorrect={canRecord ? () => startCorrection(item) : undefined} onAdverse={canReport ? () => setAdverseFor(item.id) : undefined} />
        ))}
        {sessions.data?.items.length === 0 && editing === null && (
          <Typography variant="body2" color="text.secondary">
            {t('cln:session.empty')}
          </Typography>
        )}
      </Stack>

      {editing !== null && (
        <Stack spacing={2} sx={{ mt: 2 }} data-testid="cln-session-form" data-mode={correcting ? 'correct' : 'new'}>
          <Divider />
          {defaults.error ? <ErrorState error={defaults.error} compact /> : null}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ alignItems: 'center' }}>
            {plan && (
              <Chip
                color="primary"
                label={t('cln:session.number_of_plan', { n: faDigits(num(plan.nextNumberInStage) ?? 0), total: faDigits(num(plan.stageSessions) ?? 0), plan: plan.title })}
                data-testid="cln-session-plan-number"
              />
            )}
            {!plan && data?.packageSessionNumber && (
              <Chip label={t('cln:session.package_number', { n: faDigits(num(data.packageSessionNumber) ?? 0), total: faDigits(num(data.packageSessions) ?? 0) })} data-testid="cln-session-package-number" />
            )}
            {data?.fitzpatrick ? (
              <Chip variant="outlined" label={t('cln:skin.fitzpatrick_value', { value: data.fitzpatrick })} data-testid="cln-session-fitzpatrick" />
            ) : null}
            <Button size="small" onClick={() => setSkinOpen(true)} data-testid="cln-session-skin">
              {t('cln:skin.record')}
            </Button>
          </Stack>
          {fitzpatrickMissing && (
            <Alert severity="error" data-testid="cln-session-fitzpatrick-required">
              {t('cln:session.fitzpatrick_required')}
            </Alert>
          )}
          {(data?.plans.length ?? 0) > 0 && (
            <TextField select fullWidth size="small" label={t('cln:session.plan')} value={planId} onChange={(event) => setPlanId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-session-plan' } }}>
              <MenuItem value="">{t('cln:session.no_plan')}</MenuItem>
              {data?.plans.map((option) => (
                <MenuItem key={`${option.planId}-${option.stageId ?? ''}`} value={option.planId}>
                  {option.title}
                  {option.stageTitle ? ` · ${option.stageTitle}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}

          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_event, value: typeof mode | null) => value && setMode(value)}>
            <ToggleButton value="areas" data-testid="cln-session-mode-areas">
              {t('cln:session.mode.areas')}
            </ToggleButton>
            <ToggleButton value="injection" data-testid="cln-session-mode-injection">
              {t('cln:session.mode.injection')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              {mode === 'areas' ? (
                <BodyMap selected={areas} onToggle={(code) => setAreas((current) => (current.includes(code) ? current.filter((area) => area !== code) : [...current, code]))} testId="cln-session-map" />
              ) : (
                <Stack spacing={1}>
                  <BodyMap views={['face']} pointView="face" points={mapPoints} onPoint={addPoint} testId="cln-injection-map" />
                  {pointError && (
                    <Alert severity="info" data-testid="cln-injection-point-error">
                      {pointError}
                    </Alert>
                  )}
                </Stack>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              {mode === 'areas' ? (
                <Stack spacing={1.5}>
                  <DeviceUsageField
                    branchId={encounter.branchId}
                    value={device}
                    // مقدار جلسه = اختلاف شمارنده (سرور همین را می‌سنجد)؛ مغایرت فقط وقتی مقدار مورد انتظار جداگانه داده شود.
                    onChange={(value) =>
                      setDevice(value ? { ...value, quantity: value.counterStart !== null && value.counterEnd !== null ? Math.max(0, value.counterEnd - value.counterStart) : value.quantity } : null)
                    }
                  />
                  {data?.parameterSet && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="subtitle2">{data.parameterSet.name}</Typography>
                      <MaybeSample isSample={data.parameterSet.isSample} />
                      {data.previousAt && (
                        <Typography variant="caption" color="text.secondary" data-testid="cln-session-prefill-from">
                          {t('cln:session.prefilled_from', { date: toJalaliDateTime(data.previousAt) })}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {definitions.map((definition) => (
                    <ParameterRow
                      key={definition.key}
                      definition={definition}
                      input={inputs[definition.key]}
                      previous={previousByKey.get(definition.key) ?? null}
                      onChange={(next) => setInputs((current) => ({ ...current, [definition.key]: next }))}
                    />
                  ))}
                  {!data?.parameterSet && (
                    <Typography variant="body2" color="text.secondary">
                      {t('cln:session.no_parameter_set')}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <InjectionTemplateBar template={template} onChange={setTemplate} />
                  <Stack direction="row" spacing={1}>
                    {(data?.lastInjectionMap.length ?? 0) > 0 && (
                      <Button size="small" onClick={loadPrevious} data-testid="cln-injection-load-previous">
                        {t('cln:injection.load_previous')}
                      </Button>
                    )}
                  </Stack>
                  {points.map((point, index) => (
                    <Stack key={`${index}-${point.x}-${point.y}`} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="cln-injection-point">
                      <Chip size="small" label={faDigits(index + 1)} />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {point.areaCode ? t(`cln:body_area.${point.areaCode}`) : '—'} · {point.itemName} · {faDigits(Number(point.dose))} {t(`cln:injection_unit.${point.unit}`)}
                        {point.depth ? ` · ${t(`cln:injection_depth.${point.depth}`)}` : ''}
                      </Typography>
                      <IconButton size="small" aria-label={t('cln:injection.remove')} onClick={() => setPoints((current) => current.filter((_item, position) => position !== index))}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  {points.length > 0 && (
                    <Typography variant="body2" data-testid="cln-injection-totals">
                      {t('cln:injection.totals')}:{' '}
                      {Object.entries(totals)
                        .filter(([, total]) => total > 0)
                        .map(([unit, total]) => `${faDigits(total)} ${t(`cln:injection_unit.${unit}`)}`)
                        .join(' · ')}
                    </Typography>
                  )}
                  {mismatches.map((item) => (
                    <Alert key={item.unit} severity="warning" data-testid="cln-injection-mismatch">
                      {t('cln:session.warning.injection_units_mismatch', {
                        unit: t(`cln:injection_unit.${item.unit}`),
                        expected: faDigits(Number(item.expected)),
                        recorded: faDigits(totals[item.unit] ?? 0),
                      })}
                    </Alert>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>

          <TextField fullWidth size="small" label={t('cln:session.reaction')} value={reaction} onChange={(event) => setReaction(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'cln-session-reaction' } }} />
          <TextField fullWidth size="small" label={t('cln:session.recommendations')} value={recommendations} onChange={(event) => setRecommendations(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000 } }} />
          <TextField fullWidth size="small" label={t('cln:session.outcome')} value={outcome} onChange={(event) => setOutcome(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000 } }} />
          {correcting && (
            <TextField
              fullWidth
              size="small"
              required
              label={t('cln:session.correction_reason')}
              helperText={editing.correctableUntil ? t('cln:session.correctable_until', { at: toJalaliDateTime(editing.correctableUntil) }) : undefined}
              value={correctionReason}
              onChange={(event) => setCorrectionReason(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'cln-session-correction-reason' } }}
            />
          )}
          {save.error ? <ErrorState error={save.error} compact /> : null}
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={reset} disabled={save.isPending}>
              {t('cln:common.cancel')}
            </Button>
            <Button variant="contained" onClick={() => save.mutate()} disabled={blocked || save.isPending} data-testid="cln-session-save">
              {correcting ? t('cln:session.save_correction') : t('cln:session.save')}
            </Button>
          </Stack>
        </Stack>
      )}
      {skinOpen && <SkinAssessmentDialog patientId={encounter.patientId} encounterId={encounter.id} current={data?.fitzpatrick ?? null} onClose={() => setSkinOpen(false)} onSaved={() => void defaults.refetch()} />}
      {adverseFor && <AdverseEventDialog patientId={encounter.patientId} encounterId={encounter.id} sessionId={adverseFor} onClose={() => setAdverseFor(null)} />}
    </Paper>
  );
}
